import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import { apiUrl } from '../../../environments/environment';

/**
 * The candidate's own page — **with no login**.
 *
 * ── Why there is no account ──────────────────────────────────────────────
 * Five out of 200 applicants join. Creating an account for everyone means 195
 * dead accounts, their roles, their password resets. So this takes the same
 * approach as the signing page: the token is the key.
 *
 * ── Do daraje ───────────────────────────────────────────────────────────
 *   token        → how far I have got, and when the next interview is
 *   token + OTP  → the offer amount and the offer letter
 *
 * Asking for a code every time just to see status is too strict — people would
 * stop opening the link at all. But **money is a different matter**: if the
 * link falls into somebody else's hands, they must not see a salary figure.
 */
@Component({
  selector: 'app-candidate-track',
  templateUrl: './candidate-track.component.html',
  styleUrls: ['./candidate-track.component.scss']
})
export class CandidateTrackComponent implements OnInit {

  token = '';
  loading = true;
  busy = false;

  error: string | null = null;
  expired = false;
  data: any = null;

  // ── OTP ─────────────────────────────────────────────────────────────────
  askingOtp = false;
  sentTo = '';
  code = '';
  otpError: string | null = null;
  otpNote: string | null = null;

  // ── withdraw ────────────────────────────────────────────────────────────
  showWithdraw = false;
  withdrawReason = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient
  ) { }

  async ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    await this.load();
  }

  private post(path: string, body: any = {}) {
    return this.http.post<any>(apiUrl + '/api/public/candidate/' + path,
      { token: this.token, ...body }).toPromise();
  }

  async load() {
    this.loading = true;
    try {
      const r: any = await this.post('view');
      if (!r || r.status !== 1) {
        this.error = (r && r.msg) || 'This link is not valid.';
        this.expired = !!(r && r.expired);
      } else {
        this.data = r;
        this.error = null;
      }
    } catch (e) {
      this.error = 'We could not open this page. Please try again in a moment.';
    }
    this.loading = false;
  }

  // ── OTP ─────────────────────────────────────────────────────────────────
  async askCode() {
    this.busy = true;
    this.otpError = null;
    try {
      const r: any = await this.post('otp/request');
      if (!r || r.status !== 1) { this.otpError = (r && r.msg) || 'Could not send the code.'; }
      else {
        this.askingOtp = true;
        this.sentTo = r.sent_to;
        // The code goes to the address on their record — never to one typed on
        // screen. And they are told so plainly.
        this.otpNote = `We sent a 6-digit code to ${r.sent_to}. It works for ${r.expires_in_minutes} minutes.`;
      }
    } catch (e) { this.otpError = 'Could not send the code.'; }
    this.busy = false;
  }

  async verify() {
    if (!/^\d{6}$/.test(this.code.trim())) { this.otpError = 'The code is 6 digits.'; return; }
    this.busy = true;
    this.otpError = null;
    try {
      const r: any = await this.post('otp/verify', { code: this.code.trim() });
      if (!r || r.status !== 1) { this.otpError = (r && r.msg) || 'That code is not right.'; }
      else { this.askingOtp = false; this.code = ''; this.otpNote = null; await this.load(); }
    } catch (e) { this.otpError = 'Could not check that code.'; }
    this.busy = false;
  }

  async withdraw() {
    this.busy = true;
    try {
      const r: any = await this.post('withdraw', { reason: this.withdrawReason });
      if (!r || r.status !== 1) { this.error = (r && r.msg) || 'Could not record that.'; }
      else { this.showWithdraw = false; await this.load(); }
    } catch (e) { this.error = 'Could not record that.'; }
    this.busy = false;
  }

  /** The offer letter opens on the same signing page where it is signed. */
  get letterUrl(): string {
    const t = this.data && this.data.offer && this.data.offer.letter_token;
    return t ? '#/sign/' + t : '';
  }

  money(v: any, cur = 'INR'): string {
    if (v == null || !Number.isFinite(Number(v))) return '';
    try {
      return new Intl.NumberFormat('en-IN',
        { style: 'currency', currency: cur || 'INR', maximumFractionDigits: 0 }).format(Number(v));
    } catch (e) { return String(Math.round(Number(v))); }
  }

  /** Which stage has been reached — for the strip. */
  get steps(): any[] {
    const s = this.data ? this.data.stage : '';
    const order = ['applied', 'screening', 'interviewing', 'offered', 'hired'];
    const at = order.indexOf(s);
    // Showing the strip on reject/withdraw reads as unkind — one line is enough.
    if (at < 0) return [];
    return [
      { key: 'applied', label: 'Applied' },
      { key: 'screening', label: 'Reviewed' },
      { key: 'interviewing', label: 'Interviews' },
      { key: 'offered', label: 'Offer' },
      { key: 'hired', label: 'Joining' },
    ].map((x, i) => ({ ...x, done: i < at, now: i === at }));
  }
}
